import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
  Grid,
  IconButton,
  Badge,
  TextField,
  useMediaQuery,
  useTheme,
  Paper,
} from "@mui/material";
import { useAppSelector, useAppDispatch } from "../../../hooks/hooks";
import {
  fetchGiftBrands,
  getRegisteredWebhookUrl,
  createGiftCardsWebhook,
} from "../../../redux/giftBrandsSlice";
import { addGiftBrand } from "../../../redux/giftShoppingCartSlice";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ShoppingCart from "../../ShoppingCart";
import { setSelectedMarketplaceTab } from "../../../redux/uiSlice";
import CheckoutForm from "../../CheckoutForm";

// Get width from environment variable with fallback
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

const MarketView: React.FC = () => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDebugButtons] = useState(false);
  const { giftBrands /*, registeredWebhooks */ } = useAppSelector(
    (state) => state.giftBrands
  );
  const { giftBrands: cartGiftBrands, loading } = useAppSelector(
    (state) => state.shoppingCart
  );
  const { selectedMarketplaceTab } = useAppSelector((state) => state.ui);
  const dispatch = useAppDispatch();

  const filteredBrands = giftBrands.filter(
    (brand) =>
      brand.brandName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      brand.countryName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateWebhook = async () => {
    await dispatch(
      createGiftCardsWebhook({
        url: "https://webhook.site/8892ffb5-266c-4d01-93fe-dafcaf095342",
        authorizationHeaderName: "Authorization",
      })
    );
  };
  // const loadMoreBrands = async () => {
  //   await dispatch(fetchGiftBrands({ countryCode: "CA", currentPage: 1 }));
  // };

  const scrollableBoxStyles = {
    maxHeight: isMobileView ? "76vh" : "68vh",
    overflowY: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
  };

  const handleGetWebhooks = async () => {
    // Placeholder for webhook fetching logic
    dispatch(getRegisteredWebhookUrl());
  };

  useEffect(() => {
    // const fetchBrands = async () => {
    //   try {
    //     await
    //   } catch (error) {
    //     console.error("Failed to fetch gift brands:", error);
    //   }
    // };

    if (selectedMarketplaceTab === "list") {
      dispatch(fetchGiftBrands({ countryCode: "CA", currentPage: 1 }));
    }
  }, [dispatch]);

  // useEffect(() => {
  //   dispatch(getRegisteredWebhookUrl());
  // }, [dispatch]);

  // useEffect(() => {
  //   console.log("Registered Webhooks:", registeredWebhooks);
  // }, [registeredWebhooks]);

  return (
    <Paper
      sx={{
        width: "100%",
        maxWidth: MOBILE_APP_WIDTH,
        margin: "auto",
        height: "100%",
        overflow: "hidden",
        // Mobile styles
        ...(isMobileView && {
          maxWidth: "100%",
          borderRadius: 0,
          boxShadow: "none",
        }),
        // Desktop styles
        ...(!isMobileView && {
          borderRadius: 2,
          boxShadow: 3,
        }),
      }}
    >
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" size={60} />
        </Box>
      )}
      <Box sx={{ p: isMobileView ? 1 : 2, height: "100%" }}>
        <Box sx={{ p: isMobileView ? 1 : 2, textAlign: "start" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontSize: isMobileView ? "1.5rem" : "2rem" }}
            >
              Gift Card Marketplace
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              aria-label="cart"
              onClick={() => dispatch(setSelectedMarketplaceTab("cart"))}
              size={isMobileView ? "small" : "medium"}
            >
              <Badge
                badgeContent={cartGiftBrands.length}
                color="error"
                overlap="circular"
                sx={{
                  "& .MuiBadge-badge": {
                    right: isMobileView ? 4 : 8,
                    top: isMobileView ? 4 : 8,
                    padding: "0 2px",
                    minWidth: "18px",
                    height: "18px",
                    fontSize: "0.7rem",
                  },
                }}
              >
                <ShoppingCartIcon sx={{ fontSize: isMobileView ? 24 : 32 }} />
              </Badge>
            </IconButton>
          </Box>

          <Typography
            variant="body1"
            gutterBottom
            sx={{ fontSize: isMobileView ? "0.875rem" : "1rem" }}
          >
            Browse and select from our collection of gift cards
          </Typography>
          {showDebugButtons && (
            <Box
              sx={{
                flexDirection: "row",
                display: "flex",
                justifyContent: "flex-start",
              }}
            >
              <Button onClick={handleGetWebhooks}>Get Webhooks</Button>
              <Button onClick={handleCreateWebhook}>Create Webhook</Button>
            </Box>
          )}
        </Box>
        <Divider />

        {selectedMarketplaceTab === "list" && (
          <>
            <Box sx={{ p: isMobileView ? 1 : 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Find brands..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size={isMobileView ? "small" : "medium"}
                sx={{ mb: 2 }}
              />
            </Box>

            <Grid
              container
              spacing={isMobileView ? 1 : 2}
              sx={{ ...scrollableBoxStyles, mb: 2, px: isMobileView ? 1 : 2 }}
            >
              {filteredBrands.map((brand) => (
                <Grid key={brand.productId}>
                  <Paper
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      p: isMobileView ? 1 : 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      height: "100%",
                      width: isMobileView ? "398px" : "450px",
                      "&:hover": {
                        boxShadow: isMobileView ? 0 : 2,
                        transform: isMobileView ? "none" : "translateY(-2px)",
                        transition: isMobileView ? "none" : "all 0.3s ease",
                      },
                    }}
                    elevation={0}
                  >
                    <img
                      src={brand.productImage}
                      alt={brand.brandName}
                      style={{
                        width: "100%",
                        height: isMobileView ? 100 : 150,
                        objectFit: "contain",
                        borderRadius: 4,
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mt: 1,
                        textAlign: "center",
                        fontSize: isMobileView ? "0.875rem" : "1rem",
                      }}
                    >
                      {brand.brandName}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        dispatch(setSelectedMarketplaceTab("cart"));
                        dispatch(addGiftBrand(brand));
                      }}
                      fullWidth
                      size={isMobileView ? "small" : "medium"}
                      sx={{
                        mt: "auto",
                        fontSize: isMobileView ? "0.75rem" : "0.875rem",
                      }}
                    >
                      Buy
                    </Button>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {selectedMarketplaceTab === "cart" && <ShoppingCart />}
        {selectedMarketplaceTab === "checkout" && <CheckoutForm />}
      </Box>
    </Paper>
  );
};

export default MarketView;
